import React, { ReactNode } from 'react';
import { ThemeProvider } from '../contexts/ThemeContext';
import { EventsProvider } from '../contexts/EventsContext';
import { AdminProvider } from '../contexts/AdminContext';
import { SheetSyncProvider } from '../contexts/SheetSyncContext';
import { ToastProvider } from './ui/Toast';

/**
 * AppProviders composes the context providers every route needs, in the
 * correct nesting order. ThemeProvider is outermost so light/dark is
 * available everywhere. SheetSyncProvider sits above the domain providers
 * because they consume its refresh signal (useSyncSignal) to refetch when a
 * Google-Sheets sync lands. ToastProvider is innermost so its fixed viewport
 * sits above app content and any provider consumer can fire toasts.
 * YearProvider is NOT included here — it stays in App.tsx wrapping this component.
 *
 * Donations/MonkVisits/MaintenanceTasks/VasTeams/VasBudget are admin-surface
 * (or admin+offerings-surface) data and are NOT mounted here — every route
 * used to pay for their Firestore fetches even on the public homepage. They're
 * scoped to the routes that actually consume them via AdminProviders
 * (see App.tsx) and DonationsProvider wrapping the offerings route directly.
 */
export const AppProviders: React.FC<{ children: ReactNode }> = ({ children }) => {
    return (
        <ThemeProvider>
            <SheetSyncProvider>
                <EventsProvider>
                    <AdminProvider>
                        <ToastProvider>
                            {children}
                        </ToastProvider>
                    </AdminProvider>
                </EventsProvider>
            </SheetSyncProvider>
        </ThemeProvider>
    );
};
